import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ScienceIcon from "@mui/icons-material/Science";
import { apiClient, ensureTokenIsSet } from "../../services/apiClient";
import {
  PresenceCameraConfig,
  PresenceObject,
  PresenceObjectCheckResult,
  PresenceObjectType,
  PresenceZone,
} from "../../types/api-types";

type Point = [number, number];

const TYPE_LABELS: Record<PresenceObjectType, string> = {
  lift: "Hebebühne",
  workplace: "Arbeitsplatz",
  gate: "Tor (offen/zu)",
  presence: "Büro (Person)",
};

const TYPE_COLORS: Record<PresenceObjectType, string> = {
  lift: "#00c8ff",
  workplace: "#ffb300",
  gate: "#ffe600",
  presence: "#80ff80",
};

const STATUS_LABELS: Record<string, string> = {
  occupied: "belegt",
  free: "frei",
  present: "Person",
  open: "offen",
  closed: "zu",
};

const emptyConfig: PresenceCameraConfig = {
  enabled: false,
  baseUrl: "",
  key: "",
  cid: "hobbygarage-ki-bot",
  warm: true,
  cameras: [],
  objects: [],
};

// Client-seitige Migration aus den Alt-Feldern (liftZones/gateZone/presence-Rolle),
// damit bereits konfigurierte Polygone als Startpunkt uebernommen werden koennen.
function migrateFromLegacy(config: PresenceCameraConfig): PresenceCameraConfig {
  const objects = new Map<string, PresenceObject>();
  const cameras = config.cameras.map((cam) => {
    const zones: PresenceZone[] = [...(cam.zones ?? [])];
    for (const lift of cam.liftZones ?? []) {
      const id = `lift-${lift.lift}`;
      if (!objects.has(id)) objects.set(id, { id, type: "lift", label: `Hebebühne ${lift.lift}` });
      if (!zones.some((z) => z.objectId === id)) zones.push({ objectId: id, polygon: lift.polygon });
    }
    if (cam.gateZone && (cam.role === "gate" || cam.role === "both")) {
      const id = `gate-${cam.id}`;
      objects.set(id, { id, type: "gate", label: `Tor ${cam.label}`.trim() });
      if (!zones.some((z) => z.objectId === id)) zones.push({ objectId: id, polygon: cam.gateZone });
    }
    if (cam.role === "presence" || cam.role === "both") {
      const id = `presence-${cam.id}`;
      objects.set(id, { id, type: "presence", label: cam.label || "Büro" });
    }
    return { ...cam, zones };
  });
  const merged: PresenceObject[] = [...(config.objects ?? [])];
  const haveIds = new Set(merged.map((o) => o.id));
  Array.from(objects.values()).forEach((obj) => {
    if (!haveIds.has(obj.id)) {
      merged.push(obj);
      haveIds.add(obj.id);
    }
  });
  return { ...config, objects: merged, cameras };
}

const ZoneEditor: React.FC = () => {
  const [config, setConfig] = useState<PresenceCameraConfig>(emptyConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedCameraId, setSelectedCameraId] = useState<string>("");
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [snapLoading, setSnapLoading] = useState(false);

  const [drawObjectId, setDrawObjectId] = useState<string>("");
  const [draft, setDraft] = useState<Point[] | null>(null);
  const [drag, setDrag] = useState<{ zoneIdx: number; ptIdx: number } | null>(null);

  const [testResult, setTestResult] = useState<PresenceObjectCheckResult | null>(null);
  const [testing, setTesting] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        ensureTokenIsSet();
        setLoading(true);
        const loaded = { ...emptyConfig, ...(await apiClient.getPresenceCameraConfig()) };
        setConfig(loaded);
        if (loaded.cameras[0]) setSelectedCameraId(loaded.cameras[0].id);
        setError(null);
      } catch (err: any) {
        setError(err.message || "Konfiguration konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const objects = config.objects ?? [];
  const camera = config.cameras.find((c) => c.id === selectedCameraId);
  const zones = camera?.zones ?? [];
  const objectById = useMemo(
    () => new Map(objects.map((o) => [o.id, o])),
    [objects]
  );

  const updateCameraZones = useCallback(
    (updater: (zones: PresenceZone[]) => PresenceZone[]) => {
      setConfig((prev) => ({
        ...prev,
        cameras: prev.cameras.map((c) =>
          c.id === selectedCameraId ? { ...c, zones: updater(c.zones ?? []) } : c
        ),
      }));
    },
    [selectedCameraId]
  );

  const pointFromEvent = (e: { clientX: number; clientY: number }): Point => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return [0, 0];
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    return [Number(x.toFixed(4)), Number(y.toFixed(4))];
  };

  const onCanvasClick = (e: React.MouseEvent) => {
    if (!draft || !drawObjectId) return;
    setDraft([...draft, pointFromEvent(e)]);
  };

  const finishDraft = () => {
    if (draft && draft.length >= 3 && drawObjectId) {
      const polygon = draft;
      const objectId = drawObjectId;
      updateCameraZones((z) => [...z, { objectId, polygon }]);
    }
    setDraft(null);
  };

  // Vertex-Dragging
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const pt = pointFromEvent(e);
      updateCameraZones((z) =>
        z.map((zone, i) =>
          i === drag.zoneIdx
            ? { ...zone, polygon: zone.polygon.map((p, j) => (j === drag.ptIdx ? pt : p)) }
            : zone
        )
      );
    };
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, updateCameraZones]);

  const addObject = (type: PresenceObjectType) => {
    const id = `${type}-${Date.now().toString(36)}`;
    const count = objects.filter((o) => o.type === type).length + 1;
    const label = `${TYPE_LABELS[type].replace(/\s*\(.*\)/, "")} ${count}`;
    setConfig((prev) => ({ ...prev, objects: [...(prev.objects ?? []), { id, type, label }] }));
  };

  const updateObject = (id: string, patch: Partial<PresenceObject>) =>
    setConfig((prev) => ({
      ...prev,
      objects: (prev.objects ?? []).map((o) => (o.id === id ? { ...o, ...patch } : o)),
    }));

  const removeObject = (id: string) =>
    setConfig((prev) => ({
      ...prev,
      objects: (prev.objects ?? []).filter((o) => o.id !== id),
      cameras: prev.cameras.map((c) => ({ ...c, zones: (c.zones ?? []).filter((z) => z.objectId !== id) })),
    }));

  const loadSnapshot = async () => {
    if (!selectedCameraId) return;
    try {
      setSnapLoading(true);
      setError(null);
      const res = await apiClient.getPresenceCameraSnapshot(selectedCameraId, config);
      setSnapshot(res.image);
    } catch (err: any) {
      setError(err.message || "Snapshot fehlgeschlagen");
    } finally {
      setSnapLoading(false);
    }
  };

  const runTest = async () => {
    try {
      setTesting(true);
      setError(null);
      setTestResult(await apiClient.checkPresenceObjects(config));
    } catch (err: any) {
      setError(err.message || "Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    try {
      ensureTokenIsSet();
      setSaving(true);
      setSuccess(null);
      setError(null);
      const saved = await apiClient.updatePresenceCameraConfig(config);
      setConfig({ ...emptyConfig, ...saved });
      setSuccess("Objekte & Zonen gespeichert.");
    } catch (err: any) {
      setError(err.message || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const centroid = (poly: Point[]): Point => {
    const x = poly.reduce((s, p) => s + p[0], 0) / poly.length;
    const y = poly.reduce((s, p) => s + p[1], 0) / poly.length;
    return [x, y];
  };

  // Erkannte Boxen der ausgewaehlten Kamera (px -> normiert ueber die Frame-Groesse).
  const camResult = testResult?.cameras?.find((c) => c.cameraId === selectedCameraId);
  const camW = camResult?.width || 0;
  const camH = camResult?.height || 0;
  const camPredictions = camW && camH ? camResult?.predictions ?? [] : [];

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Objekte & Zonen-Editor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lege Objekte an (Hebebühne, Arbeitsplatz, Tor, Büro), wähle eine Kamera, lade einen Snapshot
        und zeichne pro Objekt ein Polygon. Die Erkennung läuft lokal im Gateway (CPU) – Autos/Personen
        per Objekterkennung, Tor offen/zu über die Lamellenstruktur.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Objekt-Verwaltung */}
      <Typography variant="subtitle2" gutterBottom>Objekte</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
        {(["lift", "workplace", "gate", "presence"] as PresenceObjectType[]).map((t) => (
          <Button key={t} size="small" variant="outlined" onClick={() => addObject(t)}>
            + {TYPE_LABELS[t]}
          </Button>
        ))}
        <Button size="small" variant="text" onClick={() => setConfig((p) => migrateFromLegacy(p))}>
          Aus Alt-Konfiguration übernehmen
        </Button>
      </Stack>
      <Stack spacing={1} sx={{ mb: 2 }}>
        {objects.length === 0 && (
          <Typography variant="body2" color="text.secondary">Noch keine Objekte angelegt.</Typography>
        )}
        {objects.map((o) => (
          <Stack key={o.id} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: TYPE_COLORS[o.type] }} />
            <TextField
              select size="small" value={o.type} sx={{ minWidth: 150 }}
              onChange={(e) => updateObject(o.id, { type: e.target.value as PresenceObjectType })}
            >
              {(Object.keys(TYPE_LABELS) as PresenceObjectType[]).map((t) => (
                <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" value={o.label} sx={{ flex: 1 }}
              onChange={(e) => updateObject(o.id, { label: e.target.value })}
            />
            <IconButton size="small" color="error" onClick={() => removeObject(o.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Kamera + Snapshot */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
        <TextField
          select size="small" label="Kamera" value={selectedCameraId} sx={{ minWidth: 200 }}
          onChange={(e) => { setSelectedCameraId(e.target.value); setSnapshot(null); setDraft(null); }}
        >
          {config.cameras.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.label || c.id}</MenuItem>
          ))}
        </TextField>
        <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} onClick={loadSnapshot} disabled={snapLoading || !selectedCameraId}>
          {snapLoading ? "Lade…" : "Snapshot laden"}
        </Button>
        <TextField
          select size="small" label="Neue Zone für" value={drawObjectId} sx={{ minWidth: 180 }}
          onChange={(e) => setDrawObjectId(e.target.value)}
        >
          {objects.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
          ))}
        </TextField>
        {!draft ? (
          <Button size="small" variant="contained" disabled={!drawObjectId || !snapshot} onClick={() => setDraft([])}>
            Zone zeichnen
          </Button>
        ) : (
          <>
            <Button size="small" variant="contained" color="success" onClick={finishDraft}>
              Fertig ({draft.length})
            </Button>
            <Button size="small" onClick={() => setDraft(null)}>Abbrechen</Button>
          </>
        )}
        <Button size="small" variant="outlined" startIcon={<ScienceIcon />} onClick={runTest} disabled={testing}>
          {testing ? "Teste…" : "Testen"}
        </Button>
      </Stack>

      {/* Canvas */}
      <Box
        ref={containerRef}
        onClick={onCanvasClick}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 720,
          aspectRatio: "4 / 3",
          bgcolor: "#111",
          border: "1px solid #333",
          cursor: draft ? "crosshair" : "default",
          userSelect: "none",
        }}
      >
        {snapshot ? (
          <img src={snapshot} alt="Snapshot" style={{ width: "100%", height: "100%", objectFit: "fill", display: "block" }} />
        ) : (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
            <Typography variant="body2">Snapshot laden, um Zonen zu zeichnen</Typography>
          </Box>
        )}
        <svg viewBox="0 0 1 1" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
          {/* Erkannte Boxen (Test) */}
          {camPredictions.map((p, i) => (
            <rect
              key={`pred-${i}`}
              x={Math.min(p.x_min, p.x_max) / camW} y={Math.min(p.y_min, p.y_max) / camH}
              width={Math.abs(p.x_max - p.x_min) / camW} height={Math.abs(p.y_max - p.y_min) / camH}
              fill="none" stroke="#ff3b3b" strokeWidth={1} vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* Bestehende Zonen */}
          {zones.map((zone, zi) => {
            const obj = objectById.get(zone.objectId);
            const color = obj ? TYPE_COLORS[obj.type] : "#aaa";
            const pts = zone.polygon.map((p) => p.join(",")).join(" ");
            const [cx, cy] = centroid(zone.polygon);
            return (
              <g key={`zone-${zi}`}>
                <polygon points={pts} fill={color} fillOpacity={0.18} stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                <text x={cx} y={cy} fill="#fff" fontSize={0.035} textAnchor="middle" style={{ paintOrder: "stroke" }} stroke="#000" strokeWidth={0.004}>
                  {obj?.label ?? "?"}
                </text>
                {zone.polygon.map((p, pi) => (
                  <circle
                    key={pi} cx={p[0]} cy={p[1]} r={0.012}
                    fill={color} stroke="#000" strokeWidth={0.003} vectorEffect="non-scaling-stroke"
                    style={{ cursor: "grab" }}
                    onPointerDown={(e) => { e.stopPropagation(); setDrag({ zoneIdx: zi, ptIdx: pi }); }}
                  />
                ))}
              </g>
            );
          })}
          {/* Entwurf */}
          {draft && draft.length > 0 && (
            <g>
              <polyline points={draft.map((p) => p.join(",")).join(" ")} fill="none" stroke="#fff" strokeWidth={1.5} strokeDasharray="0.01,0.01" vectorEffect="non-scaling-stroke" />
              {draft.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r={0.01} fill="#fff" />
              ))}
            </g>
          )}
        </svg>
      </Box>

      {/* Zonen-Liste der Kamera */}
      <Stack spacing={0.5} sx={{ mt: 1, mb: 2 }}>
        {zones.map((zone, zi) => (
          <Stack key={zi} direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`${objectById.get(zone.objectId)?.label ?? zone.objectId} · ${zone.polygon.length} Punkte`}
              sx={{ bgcolor: objectById.get(zone.objectId) ? TYPE_COLORS[objectById.get(zone.objectId)!.type] : undefined }}
            />
            <IconButton size="small" color="error" onClick={() => updateCameraZones((z) => z.filter((_, i) => i !== zi))}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      {/* Test-Ergebnis */}
      {testResult?.objects && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Befund {testResult.liftTotal != null ? `· frei ${testResult.liftFree}/${testResult.liftTotal} Bühnen` : ""}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {testResult.objects.map((o) => (
              <Chip
                key={o.id}
                label={`${o.label}: ${STATUS_LABELS[o.status] ?? o.status}${o.confidence ? ` (${Math.round(o.confidence * 100)}%)` : ""}${o.uncertain ? " ?" : ""}`}
                color={o.status === "occupied" || o.status === "open" || o.status === "present" ? "warning" : "success"}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}

      <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
        {saving ? "Speichere…" : "Speichern"}
      </Button>
    </Paper>
  );
};

export default ZoneEditor;
